import { PhotoStudioHistoryEntry, PhotoStudioHistoryState } from '../hooks/usePhotoStudioHistory.hook'

/**
 * Which image a Lab SDK order runs on — the Lab Admin Playground's Run menu, transposed to the Photo Studio.
 * Every Lab result records the image it was generated from (`PhotoStudioHistoryEntry.sourceImageUrl`), so all
 * three choices derive from the history and follow undo/redo.
 */
export enum LabRunSource {
  /** Chain on the last output (default) */
  AFTER = 'after',
  /** Start again from the image the shown result was generated from */
  BEFORE = 'before',
  /** Back to the image that started this chain of generations */
  ORIGINAL = 'original',
}

export interface LabRunSources {
  /** The image on stage — the last output when a result exists */
  after: string
  /** The image the shown result was generated from (the original when nothing was generated yet) */
  before: string
  /** The first image of the current chain: the nearest ancestor that is not itself a Lab result */
  original: string
  /** A Lab result is on stage, so there is something to chain on — the run-source menu is offered */
  canChain: boolean
  /**
   * From the 2nd chained run on: the before is itself an output, so the origin is a distinct third choice and
   * "before" stops meaning "original".
   */
  originDiffers: boolean
}

/** The latest history entry showing `imageUrl` that is itself a Lab result — the link to follow back a chain. */
const findLabResultEntry = (entries: PhotoStudioHistoryEntry[], imageUrl: string): PhotoStudioHistoryEntry | undefined => {
  for (let index = entries.length - 1; index >= 0; index -= 1) {
    const entry = entries[index]
    if (entry.imageUrl === imageUrl && entry.sourceImageUrl) return entry
  }
  return undefined
}

/**
 * Resolve the After / Before / Original candidates for the shown image.
 *
 * - After is the preview itself.
 * - Before is the recorded source of the shown Lab result. Versions without a source link (older entries, the
 *   pre-run snapshot) fall back to the nearest earlier version with a different image, then to the original.
 * - Original follows the source links back until an image that is not a Lab result — the chain's first input.
 */
export const resolveLabRunSources = (historyState: PhotoStudioHistoryState, previewImage: string, originalImageUrl: string): LabRunSources => {
  const { entries, current } = historyState
  const shown = current && current.imageUrl === previewImage ? current : undefined

  let before = shown?.sourceImageUrl
  if (!before) {
    const currentIndex = current ? entries.indexOf(current) : -1
    for (let index = currentIndex - 1; index >= 0; index -= 1) {
      if (entries[index].imageUrl !== previewImage) {
        before = entries[index].imageUrl
        break
      }
    }
  }
  before = before ?? originalImageUrl

  let original = before
  const visited = new Set<string>([previewImage, original])
  let ancestor = findLabResultEntry(entries, original)
  while (ancestor?.sourceImageUrl && !visited.has(ancestor.sourceImageUrl)) {
    original = ancestor.sourceImageUrl
    visited.add(original)
    ancestor = findLabResultEntry(entries, original)
  }

  return {
    after: previewImage,
    before,
    original,
    canChain: !!shown?.sourceImageUrl,
    originDiffers: original !== before,
  }
}

/** The image a run consumes for a given menu choice (the After — the preview — when nothing was chosen). */
export const resolveLabRunSourceImage = (sources: LabRunSources, source: LabRunSource = LabRunSource.AFTER): string => {
  if (source === LabRunSource.BEFORE) return sources.before
  if (source === LabRunSource.ORIGINAL) return sources.original
  return sources.after
}

interface LabWorkingImageParams {
  isLabSdkEngine: boolean
  /** The view switch reads Before */
  isOriginalVisualType: boolean
  /** There is an earlier version to show as Before */
  isLabCompareAvailable: boolean
  /** The shown result's Before (`LabRunSources.before`) */
  before: string
  previewImage: string
}

/**
 * The version the Lab tools work on: the shown result's Before while the view switch reads Before — Generate then
 * reuses that earlier version, as the chevron's Before does, without undoing anything — the current one otherwise.
 */
export const resolveLabWorkingImage = ({ isLabSdkEngine, isOriginalVisualType, isLabCompareAvailable, before, previewImage }: LabWorkingImageParams): string =>
  isLabSdkEngine && isOriginalVisualType && isLabCompareAvailable ? before : previewImage

/** A run without a source picked from the chevron starts from the version on screen: Before on the Before view */
export const resolveLabDefaultRunSource = (source: LabRunSource | undefined, isLabBeforeView: boolean): LabRunSource =>
  source ?? (isLabBeforeView ? LabRunSource.BEFORE : LabRunSource.AFTER)
